// Lib
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// Components
import FloatingPanel from '../../../../../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../../../../../components/popupPanel/PopupPanelMouseManager';
import LabelEditContent from '../edit/LabelEditContent';
import LabelEditMovePopup from './LabelEditMovePopup';
import LabelColorPopup from './LabelColorPopup';

import { LabelEditContentProps } from '../labelComponentPropTypes';
import { getLabelUsageData } from '../../../../../../element/labels/labelsService';
import { fetchElements } from '../../../../../../element/elementService';
import { getElementIdsForLabelUpdate, getHighestLabelBoardId, partitionLabelRootsByEditability } from '../labelUtils';
import { updateLabel } from '../../../../../../../common/labels/labelActions';
import { createNewLabelThunk, replaceLabelIdForElementsThunk } from '../labelThunks';
import useThunkDispatch from '../../../../../../store/useThunkDispatch';

// Constants
import { TooltipAlignment, TooltipPositions } from '../../../../../../components/tooltips/tooltipConstants';
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';

// Types
import { Label } from '../../../../../../../common/labels/labelTypes';
import {
    BoardVirtualPathMap,
    LabeledElementsData,
} from '../../../../../../../common/api/labels/labelElementsRetrievalRouteTypes';

type Props = Omit<LabelEditContentProps, 'labeledElements' | 'boardVirtualPathToRootMap' | 'updateLabel'> & {
    showEditingPopup: boolean;
};

const LabelEditPopup = (props: Props) => {
    const { label, showEditingPopup, userRootBoardId, userRootPath, currentUserId } = props;

    const dispatch = useDispatch();
    const thunkDispatch = useThunkDispatch();

    const [labeledElements, setLabeledElements] = useState<LabeledElementsData[]>([]);
    const [boardVirtualPathToRootMap, setBoardVirtualPathToRootMap] = useState<BoardVirtualPathMap | null>(null);

    useEffect(() => {
        if (!label) return;

        const fetchUsageData = async () => {
            const { labeledElements, boardVirtualPathToRootMap } = await getLabelUsageData(label.id, userRootBoardId);
            setLabeledElements(labeledElements);
            setBoardVirtualPathToRootMap(boardVirtualPathToRootMap);

            dispatch(fetchElements({ elementIds: labeledElements.map((el) => el.elementId) }));
        };

        fetchUsageData();
    }, [label]);

    if (!label) return null;

    const updateLabelHandler = (changes: Partial<Label>) => {
        if (!label || !userRootPath || !boardVirtualPathToRootMap) return;

        const { labelRootsToUpdate, labelRootsNotToUpdate } = partitionLabelRootsByEditability(
            label,
            userRootPath,
            boardVirtualPathToRootMap,
        );

        // If we want to update all label roots with the changes, we can directly update the label
        if (labelRootsNotToUpdate.length === 0) {
            dispatch(updateLabel(label.id, changes));
            return;
        }

        // Otherwise remove those label roots from the old label
        dispatch(updateLabel(label.id, { rootBoardIds: labelRootsNotToUpdate }));

        // Create a new label with the updated details
        const newLabel = thunkDispatch(
            createNewLabelThunk({
                ...label,
                ...changes,
                rootBoardIds: labelRootsToUpdate,
                creator: currentUserId,
            }),
        );

        // And swap all labeled elements with the old label to the new label for those roots
        const highestRootId = getHighestLabelBoardId(label, userRootPath);
        const elementIdsForUpdate = getElementIdsForLabelUpdate(
            labeledElements,
            highestRootId,
            boardVirtualPathToRootMap,
        );
        dispatch(replaceLabelIdForElementsThunk(elementIdsForUpdate, label.id, newLabel.id));
    };

    return (
        <FloatingPanel
            className="LabelEditPopup light !w-auto !p-0 !overflow-hidden"
            selector={`#label-${label.id}`}
            pollPosition
            offset={20}
            showArrow={false}
            distance={20}
            alignment={TooltipAlignment.START}
            position={TooltipPositions.RIGHT}
            show={showEditingPopup}
        >
            <PopupPanelMouseManager visible={showEditingPopup} popupId={PopupIds.LABEL_EDIT_MENU}>
                <div className="-mx-[2.4rem]">
                    <LabelEditContent
                        {...props}
                        labeledElements={labeledElements}
                        boardVirtualPathToRootMap={boardVirtualPathToRootMap}
                        updateLabel={updateLabelHandler}
                    />
                </div>
                <LabelEditMovePopup
                    {...props}
                    labeledElements={labeledElements}
                    boardVirtualPathToRootMap={boardVirtualPathToRootMap}
                    updateLabel={updateLabelHandler}
                />
                <LabelColorPopup {...props} updateLabel={updateLabelHandler} />
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
};

export default LabelEditPopup;
